import React, { ReactElement, useState } from "react";
import { Provider } from "react-redux";
import { ConnectedRouter } from "connected-react-router";
import { QueryClientProvider } from "react-query";
import { datadogLogs } from "@datadog/browser-logs";
import getUserLanguage from "i18n/utils/getUserLanguage";
import { ErrorBoundary } from "react-error-boundary";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import "./global-style.scss";
import logger from "logging/logger";
import { SplitProvider } from "services/splitService";
import { getVar } from "config";
import PageSettingProvider from "modules/pageSetting/context/pageSettingContext";
import { TrackingProvider } from "modules/tracking";
import store, { history } from "store";
import ErrorPage from "modules/app/pages/ErrorPage";

import { ApolloProvider } from "@apollo/client";

import "./i18n/config";
import client from "modules/graphql";
import { Provider as ThemeProvider, PageLoader } from "@remo-co/ui-core";
import { I18nProvider, useI18n } from "./i18n";
import { getEnvironment } from "./helpers/getEnvironment";
import { lazyLoad } from "./helpers/lazyLoader";
import { queryClient } from "./services/apiService/queryClient";

const App = lazyLoad(() => import("./modules/app/App"));

const environment = getEnvironment();

datadogLogs.init({
  clientToken: getVar("REACT_APP_DATADOG_CLIENT_TOKEN"),
  forwardErrorsToLogs: true,
  sampleRate: 100,
  env: environment,
  version: getVar("REACT_APP_VERSION"),
  service: "remo-conference-client",
});

const RemoConference = (): ReactElement => {
  const { t } = useI18n(["common"]);
  const [lang] = useState(
    "languages" in navigator
      ? getUserLanguage(navigator.languages)
      : getUserLanguage([navigator.language]),
  );

  if (!App) {
    logger.error("[App] failed to lazy load.");

    return (
      <ErrorPage
        error={{
          name: t("error.unexpected"),
          message: t("error.unknown"),
        }}
        info={{ componentStack: "App" }}
      />
    );
  }

  return (
    <ApolloProvider client={client}>
      <ThemeProvider>
        <Provider store={store}>
          <ConnectedRouter history={history}>
            <ErrorBoundary FallbackComponent={ErrorPage}>
              <PageSettingProvider>
                <React.Suspense fallback={<PageLoader message="Loading..." />}>
                  <SplitProvider>
                    <TrackingProvider>
                      <I18nProvider language={lang.toString()}>
                        <QueryClientProvider client={queryClient}>
                          <DndProvider backend={HTML5Backend}>
                            <App />
                          </DndProvider>
                        </QueryClientProvider>
                      </I18nProvider>
                    </TrackingProvider>
                  </SplitProvider>
                </React.Suspense>
              </PageSettingProvider>
            </ErrorBoundary>
          </ConnectedRouter>
        </Provider>
      </ThemeProvider>
    </ApolloProvider>
  );
};

export default RemoConference;
